import React, { ChangeEvent, FC, FormEventHandler, MouseEventHandler, useCallback, useMemo, useState } from 'react';
import styles from './styles/request-verification.scss';
import { Button, Spinner, TextInput } from 'shamrock-clover-ui';
import { t } from 'i18next';
import { LoginForms } from './Login';
import { V2LoginService } from '../../services/login.v2.service';
import { useSearchParams } from 'react-router-dom';
import { extractDomainFromUrl, validatedFinalDestination } from './utils';
import { debounce } from '@mui/material';
import { validateEmail } from '@shamrock-core/common/utils';

interface RequestVerification {
  navigate: (arg: LoginForms) => void;
  email: string;
  setEmail: (email: string) => void;
}

export const RequestVerification: FC<RequestVerification> = ({ navigate, email, setEmail }) => {
  const loginService = useMemo(() => new V2LoginService(), []);
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [sendDisabled, setSendDisabled] = useState(true);
  const checkError = useCallback(debounce(emailToTest => {
    const isEmailValid = validateEmail(emailToTest) && emailToTest.length !== 0;
    setSendDisabled(!isEmailValid);
  }, 200), []);

  const sendCode = async () => {
    setIsLoading(true);
    const finalDestinationParam = searchParams.get('finaldestination');
    const finalDestination = finalDestinationParam ? validatedFinalDestination(finalDestinationParam) : null;
    const domain = finalDestination ? extractDomainFromUrl(finalDestination) : null;
    const loweredEmail = email.toLowerCase();
    await loginService.sendVerificationCode(loweredEmail, domain);
    setIsLoading(false);
    navigate(LoginForms.MFA_LOGIN);
  };
  const emailOnChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    const trimmedEmail = value.trim();
    checkError(trimmedEmail);
    setEmail(trimmedEmail);
  };

  const onSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    if (!sendDisabled && !isLoading) {
      sendCode();
    }
  };
  const forgotPasswordHandler: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    navigate(LoginForms.FORGOT_PASSWORD);
  };
  return (
    <form onSubmit={onSubmit}>
      <div className={styles.loginHeader}>
        <h2 data-testid="login-header">{t('login')}</h2>
        <p>
          <strong>To better secure your account,</strong> provide your account email below and we will email you a
          verification code for you to log in.
        </p>
      </div>
      <div data-testid="email-field" className={styles.loginEmailContainer}>
        <TextInput
          id="email"
          label={t('email')}
          variant="filled"
          inputType="text"
          value={email}
          disabled={isLoading}
          autoComplete={false}
          onChange={emailOnChange}
          error={sendDisabled && email.length !== 0 && t('invalid_email')}
        />
      </div>
      <div className={styles.buttonContainer}>
        <Button
          buttonStyle="solid"
          disabled={sendDisabled || isLoading}
          width="100%"
          height="40px"
          data-testid="login-button"
          type="submit"
        >
          {isLoading ? <Spinner color="white" size="16" /> : <span className={styles.buttonText}>Send Code</span>}
        </Button>
        <button
          data-testid="password-reset"
          className={styles.forgotPassword}
          onClick={forgotPasswordHandler}
          type='button'
        >
          {t('forgot_password')}
        </button>
      </div>
    </form>
  );
};
